// src/pages/PrivacyPolicyPage.jsx
import React from "react";

const PrivacyPolicyPage = () => {
  return (
    <div className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 text-center mb-12 font-inter drop-shadow-md">
          Privacy Policy for Insperity Games
        </h1>
        <p className="text-gray-600 text-center text-base mb-12">
          <strong>Last Updated: June 19, 2025</strong>
          <br />
          <span className="text-red-500 font-bold">
            Disclaimer: This is a general template. For legal compliance,
            consult with a legal professional.
          </span>
        </p>

        <section className="bg-white p-10 rounded-3xl shadow-xl content-area">
          {" "}
          {/* Added content-area class */}
          <p className="text-lg leading-relaxed text-gray-700">
            <strong>Insperity Games</strong> ("we," "our," or "us") is committed
            to protecting the privacy of our users ("you"). This Privacy Policy
            explains how we collect, use, disclose, and safeguard your
            information when you use our mobile games available on Android, iOS,
            and Amazon platforms. Please read this Privacy Policy carefully. If
            you do not agree with the terms of this Privacy Policy, please do
            not access or use our games.
          </p>
          <p className="text-lg leading-relaxed text-gray-700">
            We reserve the right to make changes to this Privacy Policy at any
            time and for any reason. We will alert you about any changes by
            updating the "Last Updated" date of this Privacy Policy. You are
            encouraged to periodically review this Privacy Policy to stay
            informed of updates. You will be deemed to have been made aware of,
            will be subject to, and will be deemed to have accepted the changes
            in any revised Privacy Policy by your continued use of the
            Application after the date such revised Privacy Policy is posted.
          </p>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            1. Data We Collect
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            We may collect information about you in a variety of ways. The
            information we may collect via our games includes:
          </p>
          <h3 className="text-2xl font-semibold font-inter text-gray-800">
            Non-Personal Data:
          </h3>
          <ul className="list-disc text-lg leading-relaxed text-gray-700">
            <li>
              <strong>Usage Data:</strong> Information about how you use our
              games, such as game progress, achievements, levels played, time
              spent in game, and in-game purchases. This data is generally
              anonymized and used for analytics and improving game experience.
            </li>
            <li>
              <strong>Device Information:</strong> Technical information about
              your mobile device, such as device type, operating system, unique
              device identifiers, IP address, mobile network information, and
              crash reports. This helps us optimize performance and troubleshoot
              issues.
            </li>
            <li>
              <strong>Advertising Identifiers:</strong> Identifiers like Google
              Advertising ID (GAID) or Apple's Identifier for Advertisers
              (IDFA), which may be used by our third-party ad network partners
              for personalized advertising. You can reset these identifiers in
              your device settings.
            </li>
          </ul>
          <h3 className="text-2xl font-semibold font-inter text-gray-800">
            Personal Data (Limited):
          </h3>
          <ul className="list-disc text-lg leading-relaxed text-gray-700">
            <li>
              <strong>Email Address:</strong> If you contact us directly for
              support or inquiries, we will collect your email address to
              respond to you.
            </li>
            <li>
              <strong>In-App Purchase Information:</strong> While we do not
              directly collect your payment information (this is handled by
              Google Play, Apple App Store, or Amazon Appstore), we may receive
              information about your purchases (e.g., item bought, transaction
              ID) for support, fulfillment, and analytics.
            </li>
          </ul>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            2. How We Use Your Information
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            We use the information we collect for various purposes, including:
          </p>
          <ul className="list-disc text-lg leading-relaxed text-gray-700">
            <li>To provide, operate, and maintain our games.</li>
            <li>To improve, personalize, and expand our games.</li>
            <li>To understand and analyze how you use our games.</li>
            <li>To develop new games, features, and functionality.</li>
            <li>
              To communicate with you, either directly or through one of our
              partners, including for customer service, to provide you with
              updates and other information relating to the games, and for
              marketing and promotional purposes.
            </li>
            <li>To process your transactions and manage your orders.</li>
            <li>To find and prevent fraud.</li>
            <li>For analytics and research purposes.</li>
            <li>
              To show you relevant advertisements (via third-party ad networks).
            </li>
          </ul>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            3. Disclosure of Your Information
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            We may share information we have collected about you in certain
            situations. Your information may be disclosed as follows:
          </p>
          <ul className="list-disc text-lg leading-relaxed text-gray-700">
            <li>
              <strong>By Law or to Protect Rights:</strong> If we believe the
              release of information about you is necessary to respond to legal
              process, to investigate or remedy potential violations of our
              policies, or to protect the rights, property, or safety of others,
              we may share your information as permitted or required by any
              applicable law, rule, or regulation.
            </li>
            <li>
              <strong>Third-Party Service Providers:</strong> We may share your
              information with third parties that perform services for us or on
              our behalf, including payment processing, data analysis, email
              delivery, hosting services, customer service, and marketing
              assistance.
            </li>
            <li>
              <strong>Marketing Communications:</strong> With your consent, or
              with an opportunity for you to withdraw consent, we may share your
              information with third parties for marketing purposes.
            </li>
            <li>
              <strong>Interactions with Other Users:</strong> If you interact
              with other users of the games, those users may see your name,
              profile photo, and descriptions of your activity.
            </li>
            <li>
              <strong>Online Postings:</strong> When you post comments,
              contributions or other content to our games, your posts may be
              viewed by all users and may be publicly distributed outside the
              games in perpetuity.
            </li>
            <li>
              <strong>Affiliates:</strong> We may share your information with
              our affiliates, in which case we will require those affiliates to
              honor this Privacy Policy. Affiliates include our parent company
              and any subsidiaries, joint venture partners or other companies
              that we control or that are under common control with us.
            </li>
            <li>
              <strong>Third-Party Advertisers:</strong> We may use third-party
              advertising companies to serve ads when you visit our games. These
              companies may use information about your visits to our games and
              other websites that are contained in web cookies and other
              tracking technologies in order to provide advertisements about
              goods and services of interest to you.
            </li>
            <li>
              <strong>Sale or Bankruptcy:</strong> If we reorganize or sell all
              or a portion of our assets, undergo a merger, or are acquired by
              another entity, we may transfer your information to the successor
              entity. If we go out of business or enter bankruptcy, your
              information would be an asset transferred or acquired by a third
              party. You acknowledge that such transfers may occur and that the
              transferee may decline to honor the commitments we made in this
              Privacy Policy.
            </li>
          </ul>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            4. Tracking Technologies
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            We may use cookies, web beacons, tracking pixels, and other tracking
            technologies in the Games to help customize the Games and improve
            your experience.
          </p>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            5. Third-Party Websites and Services
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            Our games may contain links to third-party websites and applications
            of interest, including advertisements and external services, that
            are not affiliated with us. Once you have used these links to leave
            our games, any information you provide to these third parties is not
            covered by this Privacy Policy, and we cannot guarantee the safety
            and privacy of your information.
          </p>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            6. Security of Your Information
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            We use administrative, technical, and physical security measures to
            help protect your personal information. While we have taken
            reasonable steps to secure the personal information you provide to
            us, please be aware that despite our efforts, no security measures
            are perfect or impenetrable, and no method of data transmission can
            be guaranteed against any interception or other type of misuse. Any
            information disclosed online is vulnerable to interception and
            misuse by unauthorized parties. Therefore, we cannot guarantee
            complete security if you provide personal information.
          </p>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            7. Policy for Children
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            We do not knowingly collect personally identifiable information from
            children under the age of 13. If you are a parent or guardian and
            you are aware that your child has provided us with Personal
            Information, please contact us. If we become aware that we have
            collected Personal Information from a child under age 13 without
            verification of parental consent, we take steps to remove that
            information from our servers.
          </p>
          <p className="text-red-600 font-semibold text-base">
            (<strong>IMPORTANT:</strong> If your game is targeted at children
            under 13, you MUST comply with COPPA (Children's Online Privacy
            Protection Act) in the US and GDPR-K (General Data Protection
            Regulation - Kids) in Europe, and similar laws globally. This
            requires parental consent mechanisms and strict limits on data
            collection. Consult legal counsel if your game targets children.)
          </p>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            8. Controls for Do-Not-Track Features
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            Most web browsers and some mobile operating systems and mobile
            applications include a Do-Not-Track (“DNT”) feature or setting you
            can activate to signal your privacy preference not to have data
            about your online browsing activities monitored and collected. No
            uniform technology standard for recognizing and implementing DNT
            signals has been finalized. As such, we do not currently respond to
            DNT browser signals or any other mechanism that automatically
            communicates your choice not to be tracked online. If a standard for
            online tracking is adopted that we must follow in the future, we
            will inform you about that practice in a revised version of this
            Privacy Policy.
          </p>
          <h2 className="text-3xl font-bold font-inter text-gray-900">
            9. Contact Us
          </h2>
          <p className="text-lg leading-relaxed text-gray-700">
            If you have questions or comments about this Privacy Policy, please
            contact us at:
          </p>
          <p className="font-semibold text-lg text-gray-900">
            Insperity Games
            <br />
            Email: insperitygames@gmail.com
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
